import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { displayAlert } from './feedbackSlice';

import AuthService from '../services/auth.service';
import { manageError } from '../utils/helpers';

const user = JSON.parse(localStorage.getItem('user'));
const hasAccount = JSON.parse(localStorage.getItem('hasAccount'));

export const signup = createAsyncThunk(
  'auth/register',
  async ({
    firstName, lastName, email, password, organizationName,
  }, thunkAPI) => {
    try {
      const response = await AuthService.signup(
        firstName, lastName, email, password, organizationName,
      );
      return response.data;
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, thunkAPI) => {
    try {
      const data = await AuthService.login(email, password);
      return { user: data };
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      // 🔑 Le message accompagne le rejet. Sans valeur, `.unwrap()` lève
      // l'erreur interne de Redux Toolkit, dont le `message` vaut la chaîne
      // « Rejected » — et c'est ce mot anglais que l'écran de connexion
      // affichait sous le formulaire, en production, à chaque mot de passe
      // erroné. Le bandeau disait la vraie raison ; le formulaire, lui,
      // disait « Rejected ».
      return thunkAPI.rejectWithValue({ message });
    }
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  await AuthService.logout();
});

export const resetPassword = createAsyncThunk(
  'auth/password/reset',
  async ({
    email,
  }, thunkAPI) => {
    try {
      const response = await AuthService.resetPassword(email);
      return response.data;
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const resetPasswordVerify = createAsyncThunk(
  'auth/password/reset/verify',
  async ({
    code,
  }, thunkAPI) => {
    try {
      const response = await AuthService.resetPasswordVerify(code);
      return response.data;
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

export const resetPasswordVerified = createAsyncThunk(
  // 🔴 « /confirm » et non « /verify ». Les deux thunks portaient le MÊME type
  // d'action : vérifier le code, et poser le nouveau mot de passe. Avec
  // l'ancienne syntaxe objet, `{[a.fulfilled]: …, [b.fulfilled]: …}` avait deux
  // fois la même clé — le second réducteur écrasait le premier, en silence.
  // Sans conséquence tant que les quatre faisaient la même chose ; la première
  // divergence aurait été invisible. RTK 2 refuse, et c'est ainsi qu'on l'a su.
  'auth/password/reset/confirm',
  async ({
    code,
    newPassword,
  }, thunkAPI) => {
    try {
      const response = await AuthService.resetPasswordVerified(code, newPassword);
      return response.data;
    } catch (error) {
      const message = manageError(error);
      thunkAPI.dispatch(displayAlert(message, 'error'));
      return thunkAPI.rejectWithValue();
    }
  },
);

// Le drapeau dit « ce navigateur a déjà servi à quelqu'un qui a un compte ».
// Jusqu'ici, seuls les LIENS entre les deux formulaires le posaient : une
// personne qui se connectait sans jamais cliquer sur « Déjà inscrit? » repartait
// avec un navigateur qui l'ignorait, et la racine la renvoyait vers l'inscription
// à chaque retour. Une connexion réussie est pourtant la meilleure preuve qui
// soit qu'un compte existe.
const retenirLeCompte = (state) => {
  state.hasAccount = true;
  localStorage.setItem('hasAccount', 'true');
};

const initialState = user
  ? { isLoggedIn: true, user, hasAccount: true }
  : { isLoggedIn: false, user: null, hasAccount };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers:
        {
          setHasAccount: (state, action) => {
            state.hasAccount = action.payload;
            localStorage.setItem('hasAccount', action.payload);
          },
          getHasAccount: (state, action) => {
            state.hasAccount = localStorage.getItem('hasAccount') === 'true';
          },
        },
  extraReducers: (builder) => {
    builder.addCase(signup.fulfilled, (state, action) => {
      state.isLoggedIn = false;
      retenirLeCompte(state);
    });
    builder.addCase(signup.rejected, (state, action) => {
      state.isLoggedIn = false;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      retenirLeCompte(state);
    });
    builder.addCase(login.rejected, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(logout.fulfilled, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(resetPasswordVerify.fulfilled, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(resetPasswordVerify.rejected, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(resetPasswordVerified.fulfilled, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(resetPasswordVerified.rejected, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(resetPassword.fulfilled, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
    });
  },
});

const { reducer } = authSlice;
export default reducer;

export const {
  setHasAccount,
  getHasAccount,
} = authSlice.actions;
